import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Fragment, useState, type ReactNode } from "react";
import {
  ProposalActionButton,
  ProposalQuietActionButton,
  ProposalQuietActionSpan,
} from "work/proposals/_views/_shared/proposal-styled-components";

export const MORE_ACTIONS_LABEL = "More Actions";
export const MORE_ACTIONS_MENU_ID = "proposal-secondary-actions-menu";

/**
 * One thing a proposal page offers that is not the action the page exists to perform.
 *
 * `label` is the verb, in the imperative. It is the button's text while the action is shown on its
 * own and the menu text once the actions collapse, so it has to read correctly in both.
 */
export type ProposalSecondaryAction = {
  key: string;
  label: string;
  icon: ReactNode;
  /**
   * Handed the element the action was pressed from — the button itself while the actions are shown
   * side by side, and the "More Actions" button once they have collapsed.
   *
   * An action that opens a popover has to anchor it to something that is still on the page when the
   * popover appears, and a menu item is not: pressing it closes the menu, which unmounts the item.
   * Passing the anchor in is what lets Reviewers open its selector from either form.
   */
  onClick: (anchor: HTMLButtonElement) => void;
  /** The button's colour while the action is shown on its own. Ignored in the menu, where colour is decoration. */
  color?: "primary" | "secondary";
  /** Marks an action that ends something — cancel, delete, end — and colours it in both forms. */
  destructive?: boolean;
  /**
   * Marks a way out of a negotiation: drawn to be found rather than pressed (#5240).
   *
   * Its own button hugs its label and takes the row's last column instead of a full track, which
   * is how Leave Team is drawn on a work agreement. The flag says nothing about the menu — a
   * collapsed action costs no width, so there is no track for it to give back, and it is already
   * last there because it is last in this list.
   *
   * Two of these at once would claim the same column and overlap. They do not occur together:
   * Cancel Proposal asks to be the proposal's creator and End Negotiation asks not to be.
   */
  quiet?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Why, or what it will do. Shown on the button; the menu carries the label alone. */
  tooltip?: string;
  /**
   * A standing signal that belongs to this action — today, how many reviewers have still to approve.
   *
   * It rides on the collapsed control as well as on the action's own button. A badge is something
   * the page is telling the user without being asked, so putting it behind a click would take the
   * message away rather than move it. Pass nothing where there is nothing to say.
   */
  badge?: ReactNode;
};

/** Red in the menu the same way it is red as a button, and the icon takes the text's colour with it. */
const SecondaryActionMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "destructive",
})<{ destructive?: boolean }>(({ destructive, theme }) => ({
  ...(destructive && {
    color: theme.palette.error.main,
    "& .MuiListItemIcon-root": {
      color: "inherit",
    },
  }),
  "& .MuiListItemIcon-root": {
    minWidth: theme.spacing(4),
  },
}));

function actionColor(action: ProposalSecondaryAction) {
  return action.destructive ? "error" : (action.color ?? "primary");
}

/**
 * The proposal page's secondary and destructive actions: buttons of their own while the pane can seat
 * them, one "More Actions" menu once it cannot.
 *
 * A proposal page can offer nine actions at once, and every one of them was a full-width button in the
 * same wrapping grid, in the order the buttons had been added rather than in the order they matter.
 * Measured in Chromium at 1280x900 with the Comments panel open — a 688px pane — the row took three
 * lines and 102px, and Hire, which is one of the things the page exists to do, sat on the third
 * (#5239, following #4964).
 *
 * So the ones that can wait collapse, and the one that cannot goes first. `collapsed` is the caller's
 * to decide, because the width that matters is the pane's rather than the window's: the navigation
 * rail and the comments drawer both take width no media query can see. See
 * [`useWorkPaneSize`](../../../_views/use-work-pane-size.ts).
 *
 * Which actions are secondary is a judgement the page makes, not this component. The test is whether
 * the action advances the negotiation — submitting, approving, hiring, deciding on incoming changes —
 * or manages it. Managing it can wait a click; advancing it is what the user came for.
 */
export default function ProposalSecondaryActions({
  actions,
  collapsed,
  size,
}: Readonly<{
  actions: readonly ProposalSecondaryAction[];
  /** True while the pane is too narrow to seat the actions beside the action the page exists for. */
  collapsed: boolean;
  size?: "small" | "medium";
}>) {
  // The anchor is held in state rather than a ref: the menu needs the element while rendering, and a
  // ref read during render both misses the first paint and bails the component out of the React
  // Compiler.
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const menuIsOpen = Boolean(menuAnchor);

  if (actions.length === 0) return null;

  if (!collapsed) {
    return (
      <>
        {actions.map((action) => {
          // The wrapper is the grid item, so a quiet action is placed by its span and sized by its
          // button, and both halves have to be swapped together.
          const ActionSpan = action.quiet ? ProposalQuietActionSpan : "span";
          const ActionButton = action.quiet ? ProposalQuietActionButton : ProposalActionButton;
          return (
            // A disabled button emits no pointer events, so the tooltip hangs on a live wrapper — and
            // on the wrapper rather than the button, so the title cannot overwrite the button's name.
            <Tooltip enterDelay={600} key={action.key} placement="bottom" title={action.tooltip ?? ""}>
              <ActionSpan>
                <ActionButton
                  variant="outlined"
                  color={actionColor(action)}
                  size={size}
                  startIcon={action.icon}
                  endIcon={action.badge}
                  loading={action.loading ?? false}
                  disabled={action.disabled}
                  onClick={(event) => action.onClick(event.currentTarget)}
                >
                  {action.label}
                </ActionButton>
              </ActionSpan>
            </Tooltip>
          );
        })}
      </>
    );
  }

  const badges = actions.filter((action) => action.badge !== undefined);
  return (
    <span>
      <ProposalActionButton
        variant="outlined"
        color="primary"
        size={size}
        startIcon={<MoreHorizIcon />}
        endIcon={
          badges.length === 0 ? undefined : (
            <>
              {badges.map((action) => (
                <Fragment key={action.key}>{action.badge}</Fragment>
              ))}
            </>
          )
        }
        loading={false}
        // A menu whose every item is dead is a promise the page cannot keep, so the control that
        // opens it goes dead with them.
        disabled={actions.every((action) => action.disabled || action.loading)}
        aria-haspopup="menu"
        aria-expanded={menuIsOpen}
        aria-controls={menuIsOpen ? MORE_ACTIONS_MENU_ID : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        {MORE_ACTIONS_LABEL}
      </ProposalActionButton>
      <Menu
        id={MORE_ACTIONS_MENU_ID}
        anchorEl={menuAnchor}
        open={menuIsOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{ list: { "aria-label": MORE_ACTIONS_LABEL } }}
      >
        {actions.map((action) => (
          <SecondaryActionMenuItem
            key={action.key}
            destructive={action.destructive}
            disabled={Boolean(action.disabled || action.loading)}
            onClick={() => {
              // The menu closes first, so an action that opens a popover does not stack it behind a
              // Modal that marks the rest of the page `aria-hidden`.
              const anchor = menuAnchor;
              setMenuAnchor(null);
              if (anchor) action.onClick(anchor);
            }}
          >
            <ListItemIcon>{action.icon}</ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
            {action.badge}
          </SecondaryActionMenuItem>
        ))}
      </Menu>
    </span>
  );
}
